import * as React from "react";
import { Loader2, TriangleAlert, WifiOff } from "lucide-react";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { IconTile } from "./IconTile";

/**
 * Loading, empty and error states. Appearance is in `styles/ui.css`.
 *
 * `ErrorState` branches on the error's `code`, not its message text - the code is
 * the API's contract and the message is prose that gets reworded. It also
 * surfaces the `requestId`, which is the single most useful thing to have in a
 * support conversation: a screenshot becomes enough to find the exact request in
 * the logs.
 */

export function Spinner({ className }: { className?: string }) {
  return <Loader2 aria-hidden className={cn("spinner", className)} />;
}

/**
 * Loading placeholder shaped like the content it replaces.
 *
 * Deliberately not a centred spinner: a skeleton that matches the final layout
 * means the page does not reflow when data lands, and the user's eye is already
 * in the right place.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn("skeleton", className)} />;
}

/**
 * A whole viewport with one thing centred in it.
 *
 * The three screens that use it are all "we do not yet know which page you
 * should be on". `min-h-dvh` rather than `min-h-screen` because on iOS Safari
 * `100vh` is taller than the visible viewport, which pushes a centred spinner
 * below the fold on the one screen with nothing else to look at.
 *
 * `label` is rendered for screen readers only. A bare spinner announces nothing,
 * so a non-sighted user gets silence on a screen that may sit for a second or two.
 */
export function Splash({ label }: { label?: string }) {
  return (
    <div className="splash">
      <Spinner className="spinner--lg" />
      {label && <span className="sr-only">{label}</span>}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon && (
        <IconTile tone="neutral" size="2xl">
          {icon}
        </IconTile>
      )}
      <h3 className="empty-state__title">{title}</h3>
      {description && <p className="empty-state__body">{description}</p>}
      {action && <div className="empty-state__action">{action}</div>}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const apiError = error instanceof ApiError ? error : null;
  const offline = apiError?.isNetwork ?? false;

  return (
    <div role="alert" className={cn("error-state", offline && "error-state--offline", className)}>
      <div className="error-state__icon">{offline ? <WifiOff /> : <TriangleAlert />}</div>

      <p className="error-state__title">{offline ? "No connection" : "Something went wrong"}</p>

      <p className="error-state__body">
        {apiError?.message ?? (error instanceof Error ? error.message : "Please try again.")}
      </p>

      {onRetry && (
        <button type="button" onClick={onRetry} className="error-state__retry">
          Try again
        </button>
      )}

      {apiError?.requestId && <p className="error-state__ref">Reference {apiError.requestId}</p>}
    </div>
  );
}

/**
 * Inline form-level message.
 *
 * Plain text rather than a boxed callout, so it appearing does not reflow the
 * form under the user's cursor. Never a toast: a toast auto-dismisses, and a
 * failed sign-in has to stay on screen until it is dealt with.
 */
export function FormMessage({
  tone = "error",
  children,
}: {
  tone?: "error" | "info" | "success";
  children: React.ReactNode;
}) {
  if (!children) return null;

  return (
    <p role="alert" className={cn("form-message", `form-message--${tone}`)}>
      {children}
    </p>
  );
}
